export * from './performanceReview';

/**
 * @deprecated Use organizationRole + OrganizationalRole.isAdmin instead.
 * Kept temporarily for backward compatibility during migration.
 */
export enum UserRole {
  USER = 'user',
  ADMIN = 'admin',
}

/**
 * Authorization roles held in an organization, mirroring the backend `OrganizationRole` enum.
 * Hierarchy: OWNER > ADMIN > BILLING > MANAGER > MEMBER > VIEWER.
 *
 * Not to be confused with `Member.organizationRole`, which is a free-text job title
 * (e.g. "CTO", "HR & TA Manager").
 */
export enum SecurityRole {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  BILLING = 'BILLING',
  MANAGER = 'MANAGER',
  LEAD = 'LEAD',
  MEMBER = 'MEMBER',
  VIEWER = 'VIEWER',
  SUPPORT = 'SUPPORT',
}

export interface OrganizationalRole {
  id: string;
  name: string;
  isAdmin: boolean;
  displayOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface MemberSummary {
  id: string;
  name: string;
  email: string;
  jobTitle?: string;
  profilePictureUrl?: string;
}

/** @deprecated Use MemberSummary instead */
export type UserSummary = MemberSummary;

export interface DepartmentSummary {
  id: string;
  name: string;
}

export interface Member {
  id: string;
  email: string;
  name: string;
  profilePictureUrl?: string;
  memberType?: string;
  /** Free-text job/position title (e.g. "CTO"). Not an authorization role. */
  organizationRole?: string;
  /**
   * Authorization role in the current organization, as resolved by the backend on every
   * request. Only returned by `GET /members/me`; use this for permission checks.
   */
  securityRole?: SecurityRole;
  reportsTo?: MemberSummary | null;
  clients?: string[];
  hireDate?: string;
  birthDate?: string;
  startDate?: string;
  monthlyRate?: number;
  contractRate?: number;
  isActive?: boolean;
  deactivatedAt?: string;
  /** Date the member actually left (YYYY-MM-DD, back-datable). Set on deactivation. */
  offboardingDate?: string;
  /** Reason the member was off-boarded. */
  offboardingReason?: string;
  jobTitle?: string;
  department?: DepartmentSummary;
  slackUserId?: string;
  onboardingCompleted?: boolean;
  professionalDescription?: string;
  professionalHabit?: string;
  friendGroupKnownFor?: string;
  hobbies?: string;
  lifeGoal?: string;
  funFact?: string;
  careerLearning?: string;
  createdAt?: string;
  updatedAt?: string;
  phone?: string;
  timezone?: string;
  gustoUser?: GustoUserSummary | null;
  // Legacy fields still returned by backend for compatibility
  displayName?: string;
  photoURL?: string;
}

/** @deprecated Use Member instead */
export type User = Member;

export enum InvoiceStatus {
  PENDING = 'pending',
  UNDER_REVIEW = 'under_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PAID = 'paid',
}

export interface Invoice {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  fileName: string;
  fileUrl: string;
  filePath: string;
  mimeType: string;
  fileSize: number;
  status: InvoiceStatus;
  invoiceMonth: string;
  createdAt: Date;
  updatedAt: Date;
  statusChangedAt?: Date;
  reviewerId?: string;
  reviewerComments?: string;
  notes?: string;
  // Azure Document Intelligence extracted data
  wasAnalyzed?: boolean;
  extractedVendorName?: string;
  extractedCustomerName?: string;
  extractedInvoiceId?: string;
  extractedInvoiceDate?: Date;
  extractedDueDate?: Date;
  extractedSubtotal?: number;
  extractedTotalTax?: number;
  extractedInvoiceTotal?: number;
  extractedAmountDue?: number;
  extractedCurrency?: string;
  extractedItems?: any[];
  analysisConfidence?: number;
  overrideTotal?: number;
  supportingDocumentsCount?: number;
}

export interface InvoiceStatistics {
  total: number;
  pending: number;
  underReview: number;
  approved: number;
  rejected: number;
  paid: number;
}

export interface AuthResponse {
  access_token: string;
  token_type?: string;
  expires_in?: number;
  user: Member;
}

export enum AuditAction {
  CREATED = 'created',
  STATUS_CHANGED = 'status_changed',
  NOTES_UPDATED = 'notes_updated',
  REVIEWER_COMMENTS_UPDATED = 'reviewer_comments_updated',
  DELETED = 'deleted',
}

export enum KudosClassification {
  KUDOS = 'kudos',
  BIRTHDAY = 'birthday',
  WORK_ANNIVERSARY = 'work_anniversary',
  OTHER = 'other',
}

export interface AuditLog {
  id: string;
  invoiceId: string;
  action: AuditAction;
  userId: string;
  userEmail: string;
  userName: string;
  details?: string;
  oldValue?: string;
  newValue?: string;
  timestamp: Date;
}

export interface OrgChartNode {
  id: string;
  displayName: string;
  email: string;
  photoUrl?: string;
  organizationRole?: string;
  /** Client the member is grouped under in the chart (first of their client list). */
  client?: string;
  children: OrgChartNode[];
}

export interface ReportingChainUser {
  id: string;
  name: string;
  email: string;
  profilePictureUrl?: string;
  organizationRole?: string;
  // Legacy fields for backward compat
  displayName?: string;
  photoURL?: string;
}

export interface SupportingDocument {
  id: string;
  invoiceId: string;
  userId: string;
  memberName?: string;
  fileName: string;
  fileUrl: string;
  filePath: string;
  mimeType: string;
  fileSize: number;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface UpcomingBirthday {
  id: string;
  displayName: string;
  photoUrl: string | null;
  birthMonth: number;
  birthDay: number;
  daysUntilBirthday: number;
}

export interface UpcomingAnniversary {
  id: string;
  displayName: string;
  photoUrl: string | null;
  hireMonth: number;
  hireDay: number;
  yearsOfService: number;
  daysUntilAnniversary: number;
}

/** Spring Boot Page<T> response shape */
export interface SpringPageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface GustoUser {
  id: string;
  gustoUuid: string;
  gustoType?: string;
  gustoCompanyUuid?: string;
  email?: string;
  name?: string;
  jobTitle?: string;
}

export interface GustoUserSummary {
  id: string;
  name?: string;
  email?: string;
}

export interface GustoSyncStatus {
  connected: boolean;
  lastSyncType?: string;
  lastSyncStatus?: string;
  lastSyncedAt?: string;
  syncedCount?: number;
  newCount?: number;
  updatedCount?: number;
  errorMessage?: string;
}

export interface GustoMemberData {
  memberId: string;
  gustoUuid: string;
  gustoType?: string;
  name?: string;
  email?: string;
  jobTitle?: string;
  isActive: boolean;
  onboardingStatus?: string;
  gustoSyncedAt?: string;
  gustoCompanyUuid?: string;
}

export interface GustoContractorPayment {
  paymentUuid?: string;
  contractorUuid?: string;
  date?: string;
  bonus?: string;
  reimbursement?: string;
  wage?: string;
  wageType?: string;
  wageTotal?: string;
  hourlyRate?: string;
  hours?: string;
  paymentMethod?: string;
  status?: string;
}

export interface GustoContractorPaymentsPage {
  contractorUuid?: string;
  contractorName?: string;
  payments: GustoContractorPayment[];
  totalPayments: number;
  page: number;
  totalPages: number;
}

export interface GustoSyncResult {
  syncType: string;
  status: string;
  syncedCount: number;
  newCount: number;
  updatedCount: number;
  startedAt?: string;
  completedAt?: string;
}

/** Status of a vacation request through its lifecycle (matches backend VacationRequestStatus). */
export enum VacationRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

/** Defines how vacation days are accrued over time (matches backend AccrualType). */
export enum AccrualType {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  IMMEDIATE = 'IMMEDIATE',
  FLEX = 'FLEX',
}

/** A single vacation/time-off request. Mirrors backend VacationRequestResponse. */
export interface VacationRequest {
  id: string;
  organizationId: string;
  memberId: string;
  managerId?: string | null;
  startDate: string;
  endDate: string;
  workingDays: number;
  status: VacationRequestStatus;
  notes?: string;
  reviewerId?: string;
  reviewerComments?: string;
  reviewedAt?: string;
  cancellationReason?: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** A member's vacation balance for a given year. Mirrors backend VacationBalanceResponse. */
export interface VacationBalance {
  id: string;
  organizationId: string;
  memberId: string;
  year: number;
  policyId?: string;
  annualAllowance: number;
  accrued: number;
  used: number;
  pending: number;
  carriedOver: number;
  available: number;
  /** Accrual type of the resolved policy, when available. */
  accrualType?: AccrualType;
  /** True for FLEX policies with no cap (annualAllowance <= 0). */
  unlimited?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Aggregated usage/reporting row. Mirrors backend VacationUsageSummaryResponse. */
export interface VacationUsageSummary {
  memberId: string;
  memberName: string;
  accrualType: AccrualType;
  usedDays: number;
  pendingDays: number;
  year: number;
  annualAllowance?: number;
  accrued: number;
  available: number;
}

/** Per-country vacation policy. Mirrors backend VacationPolicyResponse. */
export interface VacationPolicy {
  id: string;
  organizationId?: string;
  countryCode: string;
  name: string;
  annualAllowance: number;
  accrualType: AccrualType;
  carryOverLimit: number;
  maxConsecutiveDays?: number;
  minConsecutiveDays: number;
  advanceNoticeDays: number;
  probationPeriodDays: number;
  createdAt?: string;
  updatedAt?: string;
}
